import CustomImage from '@/components/CustomImage/CustomImage.jsx';
import { getProductInfo, selectItem } from '@/utils/analytics.js';
import { enhanceProductWithSlug } from '@/utils/products/productSlugs.js';
import Link from 'next/link';
import useWindowUnderThreshold from '@/hooks/useWindowUnderThreshold';
import React, { useEffect, useState, useContext, useCallback, useRef, useMemo } from 'react';
import cn from 'classnames';
import {
  Heading,
  HeadingEnum,
  BoldEnum,
  TextEnum,
  Text,
  ProductCatalogueV2,
  OffersSnackbar,
} from '@desquared/wave-components-library';
import { setLayout } from './useCorporateLayout';
import styles from './CorporatePage.module.css';
import { getProductFromGroups } from '@/api/wave/orders';
import {
  addProduct,
  removeProduct,
  openListSideSheet,
  transformProduct,
  getProductPropsWithCleanUrl,
  getProductBasedOnSlug,
  getProductIndexBasedOnSlug,
  calculateVolumeQuality,
  countOccurrencesById,
  findMyoIdAndDetailsById,
  getBundleOfferDescription,
} from '@/utils/products';
import { BundleOfferSideSheetStore } from '@/store/sideSheet';
import { SelectedBundleOfferStore } from '@/store/selectedBundleOfferId';
import { checkListStatus } from '@/utils/lists';
import useProductTags from '@/hooks/useProductTags';
import useBundleOffersSummary from '@/hooks/useBundleOffersSummary';
import { productsForOrder } from '@/store/cart';
import { getV31AvailableProducts } from '@/api/Products';
import { DeliveryServicesContext } from '@/contexts/DeliveryServicesContext';
import { useTranslations } from 'next-intl';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { BundleOfferSnackBarOpen } from '@/store/bundleOfferSnackBar';
import { usePersonalizedProductsIds } from '@/contexts/PersonalizedProductsIdsContext.jsx';

export default function CorporatePage(props) {
  const [newProducts, setNewProducts] = useState([]);
  const observer = useRef(null);
  const [showSnackBar, setShowSnackbar] = useState(false);
  const [showBundleOffersSnackBar, setShowBundleOffersSnackBar] = useState(false);
  const [shouldRefetch, setShouldRefetch] = useState(false);

  const router = useRouter();
  const { productTags } = useProductTags();
  const [productsOrder, setProductsOrder] = useState();
  const { bundleOffersSummary } = useBundleOffersSummary();
  const { getCollectionTypes, selectedService } = useContext(DeliveryServicesContext);
  const isMobile = useWindowUnderThreshold(850);
  const t = useTranslations('common');
  const { personalizedProductsIds } = usePersonalizedProductsIds();
  const a = useTranslations('common.asides');
  const imageGalleryAriaLabels = {
    previousSlideAriaLabel: t('previous_slide_aria_label'),
    nextSlideAriaLabel: t('next_slide_aria_label'),
    closeAriaLabel: t('asides.close'),
  };

  const displayItems = items => {
    const elements = [];
    items?.forEach((item, i) => {
      elements.push(
        <div key={item + props.dataList[i].content.title + i}>
          {setLayout(item, props.dataList[i], isMobile, props.defaultProductImage, imageGalleryAriaLabels)}
        </div>
      );
    });
    return elements;
  };

  const data = useMemo(() => displayItems(props.layoutList), [props.layoutList, isMobile]);

  useEffect(() => {
    const BundleOfferSnackBarOpen$ = BundleOfferSnackBarOpen.subscribe(setShowBundleOffersSnackBar);

    return () => {
      BundleOfferSnackBarOpen$.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (props.products || props.groupId) {
      setShouldRefetch(true); // Set flag to true when props.products updates
    }
  }, [props.products, props.groupId]);

  const fetchGroupProducts = async ({ pageParam }) => {
    if (props.groupId) {
      try {
        const productsFromGroup = await getProductFromGroups({ groupId: [props.groupId], pageParam });
        return productsFromGroup;
      } catch (err) {
        console.log(err);
      }
    } else if (props.products) {
      const productsFromCSV = getV31AvailableProducts(props.products, getCollectionTypes());
      return productsFromCSV;
    }
  };

  const {
    data: products,
    error,
    fetchNextPage,
    isLoading,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['landing-page/[...slug]', router.locale, router],
    queryFn: fetchGroupProducts,
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      if (lastPage?.length === 0) {
        return undefined;
      }
      return lastPageParam + 1;
    },
    getPreviousPageParam: (firstPage, allPages, firstPageParam) => {
      if (firstPageParam <= 1) {
        return undefined;
      }
      return firstPageParam - 1;
    },
    select: data => {
      const products = [];
      data.pages.forEach(page => {
        page.forEach(product => {
          products.push(product);
        });
      });

      return products;
    },
    enabled: !!selectedService?.id && shouldRefetch,
    staleTime: 60 * 1000,
    onSuccess: () => setShouldRefetch(false), // Manages how long before data needs refetching on back navigation, page refocus e.t.c - set to 1 minute
  });

  const lastResultItemRef = useCallback(
    node => {
      if (props.products) return;
      if (!node) return;
      if (isLoading || isFetchingNextPage) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      });

      observer.current.observe(node);
    },
    [isLoading, isFetchingNextPage, fetchNextPage]
  );

  useEffect(() => {
    const productsForOrder$ = productsForOrder.subscribe(setProductsOrder);

    return () => {
      productsForOrder$.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (productsOrder && products?.length > 0) {
      const transformedProducts = products.map(product =>
        transformProduct(
          enhanceProductWithSlug(product),
          setShowSnackbar,
          null,
          productTags,
          bundleOffersSummary,
          undefined,
          undefined,
          undefined,
          personalizedProductsIds,
          a
        )
      );
      const updatedProducts = transformedProducts.map(product => {
        let details;
        if (product.productMYO) {
          details = findMyoIdAndDetailsById(productsOrder, product.id);
        }
        const { stepToUomMYO } = product.productMYO && details ? details : { stepToUomMYO: 0 };
        const numberOfProduct = calculateVolumeQuality(
          product.isWeighed,
          product,
          countOccurrencesById(productsOrder, product.id),
          product.productMYO,
          productsOrder,
          stepToUomMYO,
          false
        );
        return {
          ...product,
          modifiedUom: numberOfProduct,
          isQsSelected: numberOfProduct > 0,
        };
      });

      setNewProducts(updatedProducts);
    }
  }, [productsOrder, products, props.products, personalizedProductsIds]);

  return (
    <>
      <Heading type={HeadingEnum.HeadingL} bold={BoldEnum.Bold} classOption={cn(styles.padding)}>
        {props.title}
      </Heading>
      {props.subtitle && (
        <Text type={TextEnum.Body} classOption={cn(styles.subtitle, styles.padding)}>
          {props.subtitle}
        </Text>
      )}
      <div className={cn(styles.layout, { [styles.bottom]: props.groupId || props.products })}>{data}</div>
      {isLoading || !selectedService?.id ? <ProductCatalogueV2 isLoading loaderTilesAmount={10} products={[]} /> : null}
      {(props.groupId || props.products) && newProducts && newProducts.length > 0 && (
        <div className={cn(styles.productsWidth)}>
          <ProductCatalogueV2
            lastItemRef={lastResultItemRef}
            title={props.groupTitle ?? ''}
            products={newProducts}
            onListClick={openListSideSheet}
            checkStatus={checkListStatus}
            addProduct={product =>
              addProduct(
                product,
                false,
                {
                  item_list_id: 'corporatePage',
                  item_list_name: 'corporatePage',
                },
                false,
                t('asides.side_cart.no_comment')
              )
            }
            removeProduct={product =>
              removeProduct(
                product,
                {
                  item_list_id: 'corporatePage',
                  item_list_name: 'corporatePage',
                },
                t('asides.side_cart.no_comment')
              )
            }
            defaultQsText={t('qs.default')}
            defaultQsUnavailableText={t('qs.default_unavailable')}
            ImageComponent={CustomImage}
            offerLabelHasRibbon={process.env.offerLabelHasRibbon}
            isTileDetailsAnimated={process.env.isTileDetailsAnimated}
            ctaIcon={process.env.ctaIcon}
            listIcon={process.env.listIcon}
            LinkComponent={props => {
              const propsWithCleanUrl = getProductPropsWithCleanUrl(props);
              return (
                <Link
                  prefetch={false}
                  {...propsWithCleanUrl}
                  onClick={() => {
                    const product = getProductBasedOnSlug(products, propsWithCleanUrl?.href);

                    if (product) {
                      selectItem(getProductInfo(product?.originalValues), {
                        item_list_id: 'landing_page',
                        item_list_name: `${props?.groupTitle}`,
                        index: getProductIndexBasedOnSlug(propsWithCleanUrl.href) + 1,
                      });
                    }
                  }}
                />
              );
            }}
            defaultProductImage={props.defaultProductImage}
            forYouLabel={t('user.profile.menu.for_you')}
          />
        </div>
      )}
      {isFetchingNextPage ? <ProductCatalogueV2 products={new Array(1)} isLoading loaderTilesAmount={5} /> : null}
      {showBundleOffersSnackBar && (
        <OffersSnackbar
          emoji="🎉"
          open={showBundleOffersSnackBar}
          setOpen={BundleOfferSnackBarOpen.update}
          hasCloseOption={true}
          position="center"
          type="warning"
          autoWidth={true}
          buttonText={t('offers_snack_bar.btn_label')}
          text={getBundleOfferDescription(SelectedBundleOfferStore.getValue(), bundleOffersSummary)}
          onButtonPress={() => {
            setShowBundleOffersSnackBar(false);
            BundleOfferSnackBarOpen.update(false);
            BundleOfferSideSheetStore.update(true);
          }}
          autoHideDuration={8000}
          closeOnClickAway={true}
          hasShadow={true}
        />
      )}
    </>
  );
}
